import {format, parseISO} from 'date-fns'
import type {Language} from '@cypro/engine/database/schema'
import type {TFnType} from '@tolgee/react'
import type {LabelTranslate} from './types'

// the label is printed in a fixed format regardless of the UI locale (Figma v2, note 15)
function toDate(value: number | string) {
  return typeof value === 'string' ? parseISO(value) : new Date(value)
}

export function formatLabelDate(value: number | string) {
  return format(toDate(value), 'dd.MM.yyyy')
}

export function formatLabelDateTime(value: number) {
  return format(new Date(value), 'dd.MM.yyyy HH:mm')
}

const LABEL_LOCALES: Record<Language, string> = {EN: 'en-GB', DE: 'de-DE', RO: 'ro-RO', PL: 'pl-PL'}

// the unit names printed on the label, never the UCUM code: `[IU]` is IU / I.E. / UI / j.m. (CY-400, CY-403)
const UNIT_NAMES: Record<string, {EN: string} & Partial<Record<Language, string>>> = {
  mg: {EN: 'mg'},
  g: {EN: 'g'},
  ug: {EN: 'µg'},
  mmol: {EN: 'mmol'},
  mL: {EN: 'ml'},
  '[IU]': {EN: 'IU', DE: 'I.E.', RO: 'UI', PL: 'j.m.'},
  '10*6.[IU]': {EN: 'million IU', DE: 'Mio. I.E.', RO: 'milioane UI', PL: 'mln j.m.'},
}
// codes that mean a listed unit: the lower-case IU (CY-391) and the Krakow migration's Polish IU (CY-400)
const UNIT_ALIASES: Record<string, string> = {'[iU]': '[IU]', J_M: '[IU]'}

/** A preparation whose substance has no dose unit; printing "mg" for it could be wrong by a factor of 1000. */
export class MissingDoseUnitError extends Error {
  constructor(
    readonly substanceNames: string[],
    readonly productionNumber: string
  ) {
    super(`No dose unit is set for ${substanceNames.join(', ')} (${productionNumber}). Set it in the drug catalogue.`)
    this.name = 'MissingDoseUnitError'
  }
}

export function assertDoseUnits(
  substances: readonly {name: string; doseUnit: string | null}[],
  productionNumber: string
) {
  const missing = substances.filter(substance => !substance.doseUnit).map(substance => substance.name)
  if (missing.length) throw new MissingDoseUnitError(missing, productionNumber)
}

/** the print toast's description, with the missing dose unit in the user's language */
export function describePrintError(error: unknown, t: TFnType) {
  if (error instanceof MissingDoseUnitError) {
    return t(
      'preparation_label.missing_dose_unit',
      'No dose unit is set for {names} ({productionNumber}). Set it in the drug catalogue.',
      {names: error.substanceNames.join(', '), productionNumber: error.productionNumber}
    )
  }
  return error instanceof Error ? error.message : undefined
}

export type LabelFormat = ReturnType<typeof createLabelFormat>

/**
 * Numbers and units as the label's language writes them (CY-403): decimal comma and the language's
 * thousands separator, unit names instead of codes. The label and the preparation report both use it.
 */
export function createLabelFormat(language: Language) {
  const locale = LABEL_LOCALES[language] ?? LABEL_LOCALES.EN
  const dose = new Intl.NumberFormat(locale, {maximumFractionDigits: 2})
  const oneDecimal = new Intl.NumberFormat(locale, {minimumFractionDigits: 1, maximumFractionDigits: 1})
  const volume = new Intl.NumberFormat(locale, {maximumFractionDigits: 1})
  const concentration = new Intl.NumberFormat(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})

  function unit(ucumCode: string | null) {
    if (!ucumCode) throw new Error('No dose unit is set for this preparation')
    const names = UNIT_NAMES[UNIT_ALIASES[ucumCode] ?? ucumCode]
    return names ? (names[language] ?? names.EN) : ucumCode
  }

  return {
    dose: (value: number) => dose.format(value),
    oneDecimal: (value: number) => oneDecimal.format(value),
    volume: (value: number) => volume.format(value),
    concentration: (value: number) => concentration.format(value),
    unit,
    perMl: (ucumCode: string | null) => `${unit(ucumCode)}/ml`,
    percentDifference(actual: number, prescribed: number) {
      const percent = ((actual - prescribed) / prescribed) * 100
      const rounded = Math.round(percent * 10) / 10
      const sign = rounded > 0 ? '+' : rounded < 0 ? '-' : ''
      return `${sign}${oneDecimal.format(Math.abs(rounded))} %`
    },
  }
}

export function splitProductionNumber(value: string) {
  return {head: value.slice(0, Math.max(0, value.length - 4)), tail: value.slice(-4)}
}

export const MAX_VIAL_BATCHES = 5
export const LABEL_SEPARATOR = '  ·  '

export function formatBatches(
  vialLots: string[],
  carrierLots: string[],
  t: LabelTranslate,
  maxVialBatches = MAX_VIAL_BATCHES
) {
  const vial = [...new Set(vialLots.filter(Boolean))]
  const carrier = [...new Set(carrierLots.filter(Boolean))]
  const shown = vial.slice(0, maxVialBatches)
  const hidden = vial.length - shown.length
  const vialPart = [...shown, ...(hidden > 0 ? [t('preparation_label.more_batches', {count: hidden})] : [])].join(', ')
  const parts = [vialPart, carrier.join(', ')].filter(Boolean)

  return parts.length ? `${t('preparation_label.batches')}: ${parts.join(LABEL_SEPARATOR)}` : null
}
